import React, { useState, useEffect } from 'react';
import {
    Route, Routes, BrowserRouter, useLocation, useNavigation, useNavigate
} from "react-router-dom";
import { createRoot } from 'react-dom/client';

import { Toast } from "./components/toast";

import {DevNav} from "./pages/dev_nav";
import { DEV_MODE } from "./settings";
import { Header } from "./components/header";
import { Footer } from "./components/footer";
import { ScanQR } from "./pages/scan_qr";
import { Dashboard } from "./pages/dashboard";
import { Badge } from "./pages/badge";
import {Map} from "./pages/map";
import {Profile} from "./pages/profile";

import { Landing } from "./pages/landing";
import { CommunityVoice } from "./pages/community_voice";
import {Signup} from "./pages/signup";
import {Roadmap} from "./pages/roadmap";
import { PeakCalc } from "./pages/peak_calc";
import {Peak} from "./pages/peak";
import { PrivacyPolicy } from "./pages/privacy_policy";
import {TermsConditions} from "./pages/terms_conditions";

import { useStore } from "./store"

import {loadStripe} from '@stripe/stripe-js';

import './assets/css/style.css'
import './assets/css/color.css'
import './assets/css/responsive.css'
import './assets/scss/theme1.scss'
//import './assets/scss/theme2.scss'
import Logo from './assets/images/logo.svg'

import Util from './helpers/util';

export const App = (props) => {
    const [state, setState] = useState({
        preloaded: false,
        delay_loading: false,
        slow: false,
        err: false,
        toast: { msg: "", status: "" },
    })
    const { preloaded, delay_loading, err, slow, toast } = state

    useEffect(() => {
        setTimeout(() => {
            setState(prev => ({...prev,
                delay_loading: true,
            }))
        }, 600)

        setTimeout(() => {
            setState(prev => ({...prev,
                slow: "Communication is taking longer than expected."
            }))
        }, 3000)

        return

        //Assign the csrf token if I was given one
        if ( props.csrf.indexOf("csrf_token") < 0
             //&& props.stripe_pk.indexOf("stripe_pk") < 0
        ) {
            setCsrfIfEmpty( props.csrf )
            //setStripeKey( props.stripe_pk )
            //setStripePromise( loadStripe( props.stripe_pk ))
        }
    }, [])

    const handleLoaded = () => {
        setState(prev => ({...prev,
            preloaded: true,
        }))
    }

    const handleToast = ( msg, status ) => {
        if ( status == undefined || status == null ) {
            status = "failure"
        }

        const toast = { msg, status };
        setState(prev => ({ ...prev, toast }))
    }

    const handleToastComplete = () => {
        const toast = { msg: "", status: "" };
        setState(prev => ({ ...prev, toast }))
    }

    //Main entry into program
    return (
        <BrowserRouter>
            {(!delay_loading || !preloaded) &&
                <Preload
                    msg={err || slow}
                    handleToast={handleToast}
                    onLoaded={handleLoaded}
                />
            }
            {preloaded &&
            <Core
                handleToast={handleToast}
            />
            }

            <Toast msg={toast.msg}
                   status={toast.status}
                   timeout="5000"
                   onComplete={handleToastComplete}
            />
        </BrowserRouter>
    );
}

export const Preload = (props) => {
    const { msg, handleToast, onLoaded } = props

    const { setUsrInfo, setCsrfIfEmpty } = useStore( x => x )

    const navigate = useNavigate()

    useEffect(() => {
        Util.fetch_js('/api/human/is_valid/', {},
            js => {
                if ( js.logged_in ) {
                    setUsrInfo( js.human )

                    const path = `${window.location.pathname}`.replace(/^\//, '').replace(/\/.*$/, '').toLowerCase()
                    if ( path == '' ) {
                        navigate('/map')
                    }
                }

                setCsrfIfEmpty( js.csrf_token )
                onLoaded()
            })
    }, [])

    return (
        <div id="preloader" className="flex-all-center">
            <img src={Logo} alt="preloader" className="preload-img"/>
            <p className="preload-msg">{msg}</p>
        </div>
    )
}

//This odd nesting is required because I need to load the path and that can only happen inside the router
export const Core = (props) => {
    const { handleToast } = props

    const [state, setState] = useState({
        forced_fix: false,
        show_header: false,
        show_footer: false,
    })
    const { forced_fix, show_header, show_footer } = state

    const location = useLocation()

    useEffect(() => {
        const path = `${window.location.pathname}`.replace(/^\//, '').replace(/\/.*$/, '').toLowerCase()
        const show_header = true// (['landing', 'community-voice', 'roadmap'].indexOf(path) >= 0) || (path == '')
        const show_footer = (['landing', 'community-voice', 'roadmap', 'privacy-policy'].indexOf(path) >= 0) || (path == '')
        const forced_fix = (['map'].indexOf(path) >= 0)

        //Title controls
        if ( path == 'landing' || path == '' ) {
            document.title = `BeenThere.io`
        }
        else {
            document.title = `BeenThere.io | ${Util.namify( path )}`
        }

        //Ensure we can scroll, this is an issue if a modal was opened and the user navs away
        if ( document.body.classList.contains('no-scroll-mondays') ) {
            document.body.classList.remove('no-scroll-mondays')
        }

        //Update my state
        setState(prev => ({...prev,
            show_header,
            show_footer,
            forced_fix,
        }))
    }, [location])

    const handleChange = (e) => {
        setState(prev => ({...prev,
            [e.target.name]: e.target.value,
        }))
    }

    //Main entry into program
    return (
        <>
            {show_header &&
                <Header
                    fixed={forced_fix}
                    showToast={handleToast}
                />
            }

            <Routes>
                <Route path="/dashboard" element={
                    <Dashboard
                        showToast={handleToast} />
                }/>
                <Route path="/qr/:chain_key_uid" element={
                    <ScanQR
                        showToast={handleToast} />
                }/>
                <Route path="/signup" element={
                    <Signup
                        showToast={handleToast} />
                }/>
                <Route path="/badge/:tab?" element={
                    <Badge
                        showToast={handleToast} />
                }/>
                <Route path="/peak/" element={
                    <Peak
                        showToast={handleToast} />
                }/>
                <Route path="/profile" element={
                    <Profile
                        showToast={handleToast} />
                }/>
                <Route path='/map' element={
                    <Map
                        showToast={handleToast} />
                }/>
                <Route path='/peak-calc' element={
                    <PeakCalc
                        showToast={handleToast} />
                }/>
                {DEV_MODE &&
                    <Route path="*" element={
                        <DevNav
                            showToast={handleToast} />
                    }/>
                }

                {/* These are done, don't touch. Corporate site */}
                <Route path="/community-voice" element={
                    <CommunityVoice
                        showToast={handleToast} />
                }/>
                <Route path="/roadmap" element={
                    <Roadmap
                        showToast={handleToast} />
                }/>
                <Route path="/privacy-policy" element={
                    <PrivacyPolicy
                        showToast={handleToast} />
                }/>
                <Route path="/terms-conditions" element={
                    <TermsConditions
                        showToast={handleToast} />
                }/>
                <Route path={DEV_MODE? '/landing': '*'} element={
                    <Landing
                        showToast={handleToast} />
                }/>
                {/* End don't touch*/}
            </Routes>

            {show_footer &&
                <Footer
                    showToast={handleToast} />
            }
        </>
    );
}

//*** This is the part that inserts the react app into the dom
const app = document.getElementById('app');
const root = createRoot(app);

const csrf = app.getAttribute("csrf");
const stripe_pk = app.getAttribute("stripe_pk");

root.render(
        <App
            csrf={csrf}
            stripe_pk={stripe_pk} />
)
